import { showDeleteConfirmation, showConfirmation, showYesNo } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import {
  ChecklistTemplateId,
  ChecklistTemplateResponse,
  CompactChecklistTemplateResponse,
  ChecklistStepResponse,
  ChecklistId,
} from '@business/entities/checklist';
import { R } from '@core/utils/r';
import { TodoChecklistAPI } from '@business/api/TodoChecklist';
import { TeamId } from '@business/entities/team';
import { UserId } from '@business/entities/account';
import { of } from 'rxjs';

export class ChecklistBloc extends BaseBloc {
  private readonly checklistRepo = new Repository<CompactChecklistTemplateResponse | ChecklistTemplateResponse>({
    getItemId: checklist => checklist.id,
  });

  readonly checklists$ = this.checklistRepo.items$;
  readonly selectChecklist = this.checklistRepo.selectItem;

  readonly selectStep = (checklistId: ChecklistId, stepId: number) =>
    this.checklistRepo.selectItem(checklistId).pipe(
      map(checklist => {
        if (!checklist) return undefined;
        let allSteps = R.flatMap((checklist as ChecklistTemplateResponse).sections || [], section => section.steps);
        allSteps = [...allSteps, ...(checklist as ChecklistTemplateResponse).steps];
        // allSteps = R.merge(allSteps, (checklist as ChecklistTemplateResponse).steps);
        return allSteps.find(s => s.id === stepId);
      }),
    );

  onReset() {
    this.checklistRepo.reset();
  }

  fetchChecklists = (parentId?: ChecklistId, onlyRecurring = false) => {
    return TodoChecklistAPI.fetchTodoChecklists(parentId, onlyRecurring).pipe(
      this.checklistRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  fetchChecklist = (id: ChecklistTemplateId) => {
    return TodoChecklistAPI.fetchTodoChecklist(id).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  createChecklist = (checklist: Partial<ChecklistTemplateResponse>) => {
    return TodoChecklistAPI.createTodoChecklist(checklist).pipe(
      this.checklistRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_checklist'),
      }),
    );
  };

  updateChecklist = (id: ChecklistTemplateId, data: Partial<ChecklistTemplateResponse>) => {
    return TodoChecklistAPI.updateTodoChecklist(id, data).pipe(
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_checklist'),
      }),
    );
  };

  deleteChecklist = (id: ChecklistTemplateId) => {
    return showDeleteConfirmation('Delete Checklist', 'Do you really want to remove this checklist?').pipe(
      switchMap(() => TodoChecklistAPI.deleteTodoChecklist(id)),
      this.checklistRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_checklist'),
      }),
    );
  };

  duplicateChecklist = (id: ChecklistTemplateId) => {
    return showDeleteConfirmation('Duplicate Template', 'Do you really want to duplicate this checklist?', { btnLabel: 'Duplicate' }).pipe(
      switchMap(() => TodoChecklistAPI.duplicateTodoChecklist(id)),
      this.checklistRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('duplicate_checklist'),
      }),
    );
  };

  executeChecklist = (ChecklistTemplateId: ChecklistTemplateId) => {
    return showConfirmation('Execute Checklist', 'Do you really want to execute this checklist?').pipe(
      switchMap(() => TodoChecklistAPI.executeTodoChecklist(ChecklistTemplateId)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('execute_template'),
      }),
    );
  };

  /* Sections */
  createChecklistSection = (ChecklistTemplateId: ChecklistTemplateId, name: string) => {
    return TodoChecklistAPI.createChecklistSection(ChecklistTemplateId, name).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section'),
      }),
    );
  };

  updateChecklistSection = (ChecklistTemplateId: ChecklistTemplateId, id: number, name: string) => {
    return TodoChecklistAPI.updateChecklistSection(id, name).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section'),
      }),
    );
  };

  deleteChecklistSection = (ChecklistTemplateId: ChecklistTemplateId, id: number) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => TodoChecklistAPI.deleteChecklistSection(id)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section'),
      }),
    );
  };

  orderSections = (checklistTemplateId: ChecklistTemplateId, items: any[]) => {
    return TodoChecklistAPI.orderSections(checklistTemplateId, items).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
  };

  sortSections = (ChecklistTemplateId: ChecklistTemplateId, sectionIds: number[]) => {
    return TodoChecklistAPI.sortSections(sectionIds).pipe(
      tap(() => {
        const checklist = this.checklistRepo.getItem(ChecklistTemplateId)! as ChecklistTemplateResponse;
        const newSections = checklist.sections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.checklistRepo.updateOne({
          id: ChecklistTemplateId,
          changes: () => ({ sections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_section_sort'),
      }),
    );
  };

  /* Steps */

  createChecklistStep = (
    ChecklistTemplateId: ChecklistTemplateId,
    sectionId: number,
    data: Partial<ChecklistStepResponse>,
    parentStepId?: number,
  ) => {
    return TodoChecklistAPI.createChecklistStep(ChecklistTemplateId, sectionId, data, parentStepId).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section_step'),
      }),
    );
  };

  updateChecklistStep = (ChecklistTemplateId: ChecklistTemplateId, id: number, data: Partial<ChecklistStepResponse>) => {
    return TodoChecklistAPI.updateChecklistStep(id, data).pipe(
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section_step'),
      }),
    );
  };

  deleteChecklistStep = (ChecklistTemplateId: ChecklistTemplateId, id: number, childCount?: number) => {
    return showDeleteConfirmation(
      'Delete Step',
      `${
        childCount
          ? `You have ${childCount} ${R.plural(childCount, 'Subtask', 'Subtasks')} in this step. These ${R.plural(
              childCount,
              'Subtask',
              'Subtasks',
            )} will also be deleted with this step. Do you really want to remove this Step?`
          : 'Do you really want to remove this Step?'
      }`,
    ).pipe(
      switchMap(() => TodoChecklistAPI.deleteChecklistStep(id)),
      switchMap(() => this.fetchChecklist(ChecklistTemplateId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section_step'),
      }),
    );
  };

  sortSectionSteps = (ChecklistTemplateId: ChecklistTemplateId, sectionId: number, stepIds: number[], parentStepId?: number) => {
    return TodoChecklistAPI.sortSectionSteps(stepIds).pipe(
      tap(() => {
        const checklist = this.checklistRepo.getItem(ChecklistTemplateId)! as ChecklistTemplateResponse;
        const affectedSection = R.find(checklist.sections, s => s.id === sectionId)!;

        let noSectionSteps = false;
        let sectionSteps = [];
        if (affectedSection) {
          sectionSteps = affectedSection.steps;
        } else {
          noSectionSteps = true;
          sectionSteps = R.orderBy(checklist.steps, s => s.menuOrder, ['asc']);
        }

        const newSteps = sectionSteps.map(step => {
          if (parentStepId) {
            const newSubSteps = step.subSteps.map(subStep => ({ ...subStep, menuOrder: R.findIndex(stepIds, i => i === subStep.id) }));
            return { ...step, subSteps: R.sortBy(newSubSteps, s => s.menuOrder) };
          }
          return { ...step, menuOrder: R.findIndex(stepIds, i => i === step.id) };
        });

        const newSections = checklist.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, steps: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        const newChecklistSteps = R.sortBy(newSteps, s => s.menuOrder);

        this.checklistRepo.updateOne({
          id: ChecklistTemplateId,
          changes: () => (noSectionSteps ? { steps: newChecklistSteps } : { sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_steps_sort'),
      }),
    );
  };

  updateAssignne = (type: string, id: number | string, items: string[], assigneeAdded?: boolean) => {
    if (assigneeAdded) {
      if (type === 'template' || type === 'checklist') {
        return showYesNo('Assign User', `${'Would you like to assign all sections and steps to this user?'}`).pipe(
          switchMap(res => {
            if (res) {
              return TodoChecklistAPI.updateAssignne(type, id, items, true, true).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
            } else {
              return TodoChecklistAPI.updateAssignne(type, id, items, false, false).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
            }
          }),
          handleMessage({
            type: requestMessage('update_checklist'),
          }),
        );
      } else if (type === 'section') {
        return showYesNo('Assign User', `${'Would you like to assign all steps in this section to this user?'}`).pipe(
          switchMap(response => {
            let assignStep = false;
            if (response) {
              assignStep = true;
            }
            return TodoChecklistAPI.updateAssignne(type, id, items, false, assignStep).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
          }),
          handleMessage({
            type: requestMessage('update_checklist'),
          }),
        );
      } else {
        return TodoChecklistAPI.updateAssignne(type, id, items).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
      }
    } else {
      return TodoChecklistAPI.updateAssignne(type, id, items).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
    }
  };

  updateWatcher = (type: string, id: number | string, items: string[]) => {
    return TodoChecklistAPI.updateWatcher(type, id, items).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
  };

  selectUserTeam = (id: string, ids: string[], keepAssignee: boolean) => {
    return TodoChecklistAPI.selectUserTeam(id, ids, keepAssignee).pipe(
      switchMap(() => this.fetchChecklist(id)),
      handleMessage({
        type: requestMessage('execute_template'),
      }),
    );
  };

  search = (id: UserId | TeamId) => {
    return TodoChecklistAPI.search(id).pipe(this.checklistRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_templates_error') }));
  };

  toggleStep = (id: number, completeAll?: boolean) => {
    return TodoChecklistAPI.toggleStep(id, completeAll).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
  };

  onAssigneeSelect = (assigneeId: string, checklistId: ChecklistId, currentChecklistId: ChecklistId) => {
    return TodoChecklistAPI.onAssigneeSelect(assigneeId, checklistId, currentChecklistId).pipe(this.checklistRepo.ops.upsertOne(item => ({ item })));
  };
}
